import {
  useContractRead,
  UseContractReadConfig,
  useContractWrite,
  Address,
  UseContractWriteConfig,
  usePrepareContractWrite,
  UsePrepareContractWriteConfig,
  useContractEvent,
  UseContractEventConfig,
} from 'wagmi'
import { ReadContractResult, WriteContractMode, PrepareWriteContractResult } from 'wagmi/actions'

import {
  getContract,
  GetContractArgs,
  readContract,
  ReadContractConfig,
  writeContract,
  WriteContractArgs,
  WriteContractPreparedArgs,
  WriteContractUnpreparedArgs,
  prepareWriteContract,
  PrepareWriteContractConfig,
  watchContractEvent,
  WatchContractEventConfig,
  WatchContractEventCallback,
} from 'wagmi/actions'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Escrow
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export const escrowABI = [
  {
    stateMutability: 'nonpayable',
    type: 'constructor',
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address' },
      { name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes' },
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
      { name: '_templateRegistry', internalType: 'contract IDisputeTemplateRegistry', type: 'address' },
      { name: '_feeTimeout', internalType: 'uint256', type: 'uint256' },
      { name: '_settlementTimeout', internalType: 'uint256', type: 'uint256' },
    ],
  },
  { type: 'error', inputs: [], name: 'ArbitratorOnly' },
  { type: 'error', inputs: [], name: 'BuyerFeeNotCoverArbitrationCosts' },
  { type: 'error', inputs: [], name: 'BuyerOnly' },
  { type: 'error', inputs: [], name: 'BuyerOrSellerOnly' },
  { type: 'error', inputs: [], name: 'DeadlineNotPassed' },
  { type: 'error', inputs: [], name: 'DisputeAlreadyResolved' },
  { type: 'error', inputs: [], name: 'GovernorOnly' },
  { type: 'error', inputs: [], name: 'InvalidRuling' },
  { type: 'error', inputs: [], name: 'MaximumPaymentAmountExceeded' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnAcceptSettlement' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnPayFeeBuyer' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnPayFeeSeller' },
  { type: 'error', inputs: [], name: 'NotWaitingForBuyerFees' },
  { type: 'error', inputs: [], name: 'NotWaitingForSellerFees' },
  { type: 'error', inputs: [], name: 'SellerFeeNotCoverArbitrationCosts' },
  { type: 'error', inputs: [], name: 'SellerOnly' },
  { type: 'error', inputs: [], name: 'SettlementPeriodNotOver' },
  { type: 'error', inputs: [], name: 'TimeoutNotPassed' },
  { type: 'error', inputs: [], name: 'TransactionDisputed' },
  { type: 'error', inputs: [], name: 'TransactionEscalatedForArbitration' },
  { type: 'error', inputs: [], name: 'TransactionExpired' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address', indexed: true },
      { name: '_arbitrableDisputeID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_externalDisputeID', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_templateId', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_templateUri', internalType: 'string', type: 'string', indexed: false },
    ],
    name: 'DisputeRequest',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_party', internalType: 'enum Escrow.Party', type: 'uint8', indexed: false },
    ],
    name: 'HasToPayFee',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_feeTimeout', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_settlementTimeout', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes', indexed: false },
    ],
    name: 'ParameterUpdated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_party', internalType: 'address', type: 'address', indexed: false },
    ],
    name: 'Payment',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address', indexed: true },
      { name: '_disputeID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_ruling', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'Ruling',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_party', internalType: 'enum Escrow.Party', type: 'uint8', indexed: false },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'SettlementProposed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_transactionUri', internalType: 'string', type: 'string', indexed: false },
      { name: '_buyer', internalType: 'address', type: 'address', indexed: true },
      { name: '_seller', internalType: 'address', type: 'address', indexed: true },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_asset', internalType: 'string', type: 'string', indexed: false },
      { name: '_deadline', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'TransactionCreated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_resolution', internalType: 'enum Escrow.Resolution', type: 'uint8', indexed: true },
    ],
    name: 'TransactionResolved',
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'AMOUNT_OF_CHOICES',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'acceptSettlement',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'arbitrator',
    outputs: [{ name: '', internalType: 'contract IArbitratorV2', type: 'address' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'arbitratorExtraData',
    outputs: [{ name: '', internalType: 'bytes', type: 'bytes' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address' }],
    name: 'changeArbitrator',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes' }],
    name: 'changeArbitratorExtraData',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
    ],
    name: 'changeDisputeTemplate',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_feeTimeout', internalType: 'uint256', type: 'uint256' }],
    name: 'changeFeeTimeout',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_governor', internalType: 'address', type: 'address' }],
    name: 'changeGovernor',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_settlementTimeout', internalType: 'uint256', type: 'uint256' }],
    name: 'changeSettlementTimeout',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_templateRegistry', internalType: 'contract IDisputeTemplateRegistry', type: 'address' }],
    name: 'changeTemplateRegistry',
    outputs: [],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [
      { name: '_timeoutPayment', internalType: 'uint256', type: 'uint256' },
      { name: '_transactionUri', internalType: 'string', type: 'string' },
      { name: '_seller', internalType: 'address payable', type: 'address' },
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
    ],
    name: 'createTransaction',
    outputs: [{ name: 'transactionID', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    name: 'disputeIDtoTransactionID',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'executeTransaction',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'feeTimeout',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'getCountTransactions',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'governor',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'pay',
    outputs: [],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'payArbitrationFeeByBuyer',
    outputs: [],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'payArbitrationFeeBySeller',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'proposeSettlement',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amountReimbursed', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'reimburse',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_disputeID', internalType: 'uint256', type: 'uint256' },
      { name: '_ruling', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'rule',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'settlementTimeout',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'templateId',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'templateRegistry',
    outputs: [{ name: '', internalType: 'contract IDisputeTemplateRegistry', type: 'address' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'timeOutByBuyer',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'timeOutBySeller',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    name: 'transactions',
    outputs: [
      { name: 'buyer', internalType: 'address payable', type: 'address' },
      { name: 'seller', internalType: 'address payable', type: 'address' },
      { name: 'amount', internalType: 'uint256', type: 'uint256' },
      { name: 'settlementBuyer', internalType: 'uint256', type: 'uint256' },
      { name: 'settlementSeller', internalType: 'uint256', type: 'uint256' },
      { name: 'deadline', internalType: 'uint256', type: 'uint256' },
      { name: 'disputeID', internalType: 'uint256', type: 'uint256' },
      { name: 'buyerFee', internalType: 'uint256', type: 'uint256' },
      { name: 'sellerFee', internalType: 'uint256', type: 'uint256' },
      { name: 'lastFeePaymentTime', internalType: 'uint256', type: 'uint256' },
      { name: 'templateData', internalType: 'string', type: 'string' },
      { name: 'templateDataMappings', internalType: 'string', type: 'string' },
      { name: 'status', internalType: 'enum Escrow.Status', type: 'uint8' },
    ],
  },
] as const

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export const escrowAddress = {
  421614: '0x250AB0477346aDFC010585b58FbF61cff1d8f3ea',
} as const

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export const escrowConfig = { address: escrowAddress, abi: escrowABI } as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// EscrowToken
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export const escrowTokenABI = [
  {
    stateMutability: 'nonpayable',
    type: 'constructor',
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address' },
      { name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes' },
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
      { name: '_templateRegistry', internalType: 'contract IDisputeTemplateRegistry', type: 'address' },
      { name: '_feeTimeout', internalType: 'uint256', type: 'uint256' },
      { name: '_settlementTimeout', internalType: 'uint256', type: 'uint256' },
    ],
  },
  { type: 'error', inputs: [], name: 'ArbitratorOnly' },
  { type: 'error', inputs: [], name: 'BuyerFeeNotCoverArbitrationCosts' },
  { type: 'error', inputs: [], name: 'BuyerOnly' },
  { type: 'error', inputs: [], name: 'BuyerOrSellerOnly' },
  { type: 'error', inputs: [], name: 'DeadlineNotPassed' },
  { type: 'error', inputs: [], name: 'DisputeAlreadyResolved' },
  { type: 'error', inputs: [], name: 'GovernorOnly' },
  { type: 'error', inputs: [], name: 'InvalidRuling' },
  { type: 'error', inputs: [], name: 'MaximumPaymentAmountExceeded' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnAcceptSettlement' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnPayFeeBuyer' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnPayFeeSeller' },
  { type: 'error', inputs: [], name: 'NotWaitingForBuyerFees' },
  { type: 'error', inputs: [], name: 'NotWaitingForSellerFees' },
  { type: 'error', inputs: [], name: 'SellerFeeNotCoverArbitrationCosts' },
  { type: 'error', inputs: [], name: 'SellerOnly' },
  { type: 'error', inputs: [], name: 'SettlementPeriodNotOver' },
  { type: 'error', inputs: [], name: 'TimeoutNotPassed' },
  { type: 'error', inputs: [], name: 'TokenTransferFailed' },
  { type: 'error', inputs: [], name: 'TransactionDisputed' },
  { type: 'error', inputs: [], name: 'TransactionEscalatedForArbitration' },
  { type: 'error', inputs: [], name: 'TransactionExpired' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address', indexed: true },
      { name: '_arbitrableDisputeID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_externalDisputeID', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_templateId', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_templateUri', internalType: 'string', type: 'string', indexed: false },
    ],
    name: 'DisputeRequest',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_party', internalType: 'enum EscrowToken.Party', type: 'uint8', indexed: false },
    ],
    name: 'HasToPayFee',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_feeTimeout', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_settlementTimeout', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes', indexed: false },
    ],
    name: 'ParameterUpdated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_party', internalType: 'address', type: 'address', indexed: false },
    ],
    name: 'Payment',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address', indexed: true },
      { name: '_disputeID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_ruling', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'Ruling',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_party', internalType: 'enum EscrowToken.Party', type: 'uint8', indexed: false },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'SettlementProposed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_transactionUri', internalType: 'string', type: 'string', indexed: false },
      { name: '_buyer', internalType: 'address', type: 'address', indexed: true },
      { name: '_seller', internalType: 'address', type: 'address', indexed: true },
      { name: '_token', internalType: 'contract IERC20', type: 'address', indexed: false },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_asset', internalType: 'string', type: 'string', indexed: false },
      { name: '_deadline', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'TransactionCreated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_resolution', internalType: 'enum EscrowToken.Resolution', type: 'uint8', indexed: true },
    ],
    name: 'TransactionResolved',
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'AMOUNT_OF_CHOICES',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'acceptSettlement',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'arbitrator',
    outputs: [{ name: '', internalType: 'contract IArbitratorV2', type: 'address' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'arbitratorExtraData',
    outputs: [{ name: '', internalType: 'bytes', type: 'bytes' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address' }],
    name: 'changeArbitrator',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes' }],
    name: 'changeArbitratorExtraData',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
    ],
    name: 'changeDisputeTemplate',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_feeTimeout', internalType: 'uint256', type: 'uint256' }],
    name: 'changeFeeTimeout',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_governor', internalType: 'address', type: 'address' }],
    name: 'changeGovernor',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_settlementTimeout', internalType: 'uint256', type: 'uint256' }],
    name: 'changeSettlementTimeout',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_templateRegistry', internalType: 'contract IDisputeTemplateRegistry', type: 'address' }],
    name: 'changeTemplateRegistry',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
      { name: '_token', internalType: 'contract IERC20', type: 'address' },
      { name: '_timeoutPayment', internalType: 'uint256', type: 'uint256' },
      { name: '_transactionUri', internalType: 'string', type: 'string' },
      { name: '_seller', internalType: 'address payable', type: 'address' },
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
    ],
    name: 'createTransaction',
    outputs: [{ name: 'transactionID', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    name: 'disputeIDtoTransactionID',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'executeTransaction',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'feeTimeout',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'getCountTransactions',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'governor',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'pay',
    outputs: [],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'payArbitrationFeeByBuyer',
    outputs: [],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'payArbitrationFeeBySeller',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'proposeSettlement',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amountReimbursed', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'reimburse',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_disputeID', internalType: 'uint256', type: 'uint256' },
      { name: '_ruling', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'rule',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'settlementTimeout',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'templateId',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'templateRegistry',
    outputs: [{ name: '', internalType: 'contract IDisputeTemplateRegistry', type: 'address' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'timeOutByBuyer',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'timeOutBySeller',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    name: 'transactions',
    outputs: [
      { name: 'buyer', internalType: 'address payable', type: 'address' },
      { name: 'seller', internalType: 'address payable', type: 'address' },
      { name: 'amount', internalType: 'uint256', type: 'uint256' },
      { name: 'settlementBuyer', internalType: 'uint256', type: 'uint256' },
      { name: 'settlementSeller', internalType: 'uint256', type: 'uint256' },
      { name: 'deadline', internalType: 'uint256', type: 'uint256' },
      { name: 'disputeID', internalType: 'uint256', type: 'uint256' },
      { name: 'buyerFee', internalType: 'uint256', type: 'uint256' },
      { name: 'sellerFee', internalType: 'uint256', type: 'uint256' },
      { name: 'lastFeePaymentTime', internalType: 'uint256', type: 'uint256' },
      { name: 'templateData', internalType: 'string', type: 'string' },
      { name: 'templateDataMappings', internalType: 'string', type: 'string' },
      { name: 'status', internalType: 'enum EscrowToken.Status', type: 'uint8' },
      { name: 'token', internalType: 'contract IERC20', type: 'address' },
    ],
  },
] as const

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export const escrowTokenAddress = {
  421614: '0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D',
} as const

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export const escrowTokenConfig = { address: escrowTokenAddress, abi: escrowTokenABI } as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// EscrowUniversal
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export const escrowUniversalABI = [
  {
    stateMutability: 'nonpayable',
    type: 'constructor',
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address' },
      { name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes' },
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
      { name: '_templateRegistry', internalType: 'contract IDisputeTemplateRegistry', type: 'address' },
      { name: '_feeTimeout', internalType: 'uint256', type: 'uint256' },
      { name: '_settlementTimeout', internalType: 'uint256', type: 'uint256' },
    ],
  },
  { type: 'error', inputs: [], name: 'ArbitratorOnly' },
  { type: 'error', inputs: [], name: 'BuyerFeeNotCoverArbitrationCosts' },
  { type: 'error', inputs: [], name: 'BuyerOnly' },
  { type: 'error', inputs: [], name: 'BuyerOrSellerOnly' },
  { type: 'error', inputs: [], name: 'DeadlineNotPassed' },
  { type: 'error', inputs: [], name: 'DisputeAlreadyResolved' },
  { type: 'error', inputs: [], name: 'GovernorOnly' },
  { type: 'error', inputs: [], name: 'InvalidRuling' },
  { type: 'error', inputs: [], name: 'MaximumPaymentAmountExceeded' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnAcceptSettlement' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnPayFeeBuyer' },
  { type: 'error', inputs: [], name: 'NoSettlementProposedOrTransactionMovedOnPayFeeSeller' },
  { type: 'error', inputs: [], name: 'NotSupported' },
  { type: 'error', inputs: [], name: 'NotWaitingForBuyerFees' },
  { type: 'error', inputs: [], name: 'NotWaitingForSellerFees' },
  { type: 'error', inputs: [], name: 'SellerFeeNotCoverArbitrationCosts' },
  { type: 'error', inputs: [], name: 'SellerOnly' },
  { type: 'error', inputs: [], name: 'SettlementPeriodNotOver' },
  { type: 'error', inputs: [], name: 'TimeoutNotPassed' },
  { type: 'error', inputs: [], name: 'TokenTransferFailed' },
  { type: 'error', inputs: [], name: 'TransactionDisputed' },
  { type: 'error', inputs: [], name: 'TransactionEscalatedForArbitration' },
  { type: 'error', inputs: [], name: 'TransactionExpired' },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address', indexed: true },
      { name: '_arbitrableDisputeID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_externalDisputeID', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_templateId', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_templateUri', internalType: 'string', type: 'string', indexed: false },
    ],
    name: 'DisputeRequest',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_transactionUri', internalType: 'string', type: 'string', indexed: false },
      { name: '_buyer', internalType: 'address', type: 'address', indexed: true },
      { name: '_seller', internalType: 'address', type: 'address', indexed: true },
      { name: '_token', internalType: 'contract IERC20', type: 'address', indexed: false },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_deadline', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'ERC20TransactionCreated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_party', internalType: 'enum Party', type: 'uint8', indexed: false },
    ],
    name: 'HasToPayFee',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_transactionUri', internalType: 'string', type: 'string', indexed: false },
      { name: '_buyer', internalType: 'address', type: 'address', indexed: true },
      { name: '_seller', internalType: 'address', type: 'address', indexed: true },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_deadline', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'NativeTransactionCreated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_feeTimeout', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_settlementTimeout', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes', indexed: false },
    ],
    name: 'ParameterUpdated',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
      { name: '_party', internalType: 'address', type: 'address', indexed: false },
    ],
    name: 'Payment',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address', indexed: true },
      { name: '_disputeID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_ruling', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'Ruling',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_party', internalType: 'enum Party', type: 'uint8', indexed: false },
      { name: '_amount', internalType: 'uint256', type: 'uint256', indexed: false },
    ],
    name: 'SettlementProposed',
  },
  {
    type: 'event',
    anonymous: false,
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256', indexed: true },
      { name: '_resolution', internalType: 'enum Resolution', type: 'uint8', indexed: true },
    ],
    name: 'TransactionResolved',
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'AMOUNT_OF_CHOICES',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'acceptSettlement',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'arbitrator',
    outputs: [{ name: '', internalType: 'contract IArbitratorV2', type: 'address' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'arbitratorExtraData',
    outputs: [{ name: '', internalType: 'bytes', type: 'bytes' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_arbitrator', internalType: 'contract IArbitratorV2', type: 'address' }],
    name: 'changeArbitrator',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_arbitratorExtraData', internalType: 'bytes', type: 'bytes' }],
    name: 'changeArbitratorExtraData',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
    ],
    name: 'changeDisputeTemplate',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_feeTimeout', internalType: 'uint256', type: 'uint256' }],
    name: 'changeFeeTimeout',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_governor', internalType: 'address', type: 'address' }],
    name: 'changeGovernor',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_settlementTimeout', internalType: 'uint256', type: 'uint256' }],
    name: 'changeSettlementTimeout',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_templateRegistry', internalType: 'contract IDisputeTemplateRegistry', type: 'address' }],
    name: 'changeTemplateRegistry',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
      { name: '_token', internalType: 'contract IERC20', type: 'address' },
      { name: '_deadline', internalType: 'uint256', type: 'uint256' },
      { name: '_transactionUri', internalType: 'string', type: 'string' },
      { name: '_seller', internalType: 'address payable', type: 'address' },
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
    ],
    name: 'createERC20Transaction',
    outputs: [{ name: 'transactionID', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [
      { name: '_deadline', internalType: 'uint256', type: 'uint256' },
      { name: '_transactionUri', internalType: 'string', type: 'string' },
      { name: '_seller', internalType: 'address payable', type: 'address' },
      { name: '_templateData', internalType: 'string', type: 'string' },
      { name: '_templateDataMappings', internalType: 'string', type: 'string' },
    ],
    name: 'createNativeTransaction',
    outputs: [{ name: 'transactionID', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    name: 'disputeIDtoTransactionID',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'executeTransaction',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'feeTimeout',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'getTransactionCount',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'governor',
    outputs: [{ name: '', internalType: 'address', type: 'address' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'pay',
    outputs: [],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'payArbitrationFeeByBuyer',
    outputs: [],
  },
  {
    stateMutability: 'payable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'payArbitrationFeeBySeller',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amount', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'proposeSettlement',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_transactionID', internalType: 'uint256', type: 'uint256' },
      { name: '_amountReimbursed', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'reimburse',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [
      { name: '_disputeID', internalType: 'uint256', type: 'uint256' },
      { name: '_ruling', internalType: 'uint256', type: 'uint256' },
    ],
    name: 'rule',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'settlementTimeout',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'templateId',
    outputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [],
    name: 'templateRegistry',
    outputs: [{ name: '', internalType: 'contract IDisputeTemplateRegistry', type: 'address' }],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'timeOutByBuyer',
    outputs: [],
  },
  {
    stateMutability: 'nonpayable',
    type: 'function',
    inputs: [{ name: '_transactionID', internalType: 'uint256', type: 'uint256' }],
    name: 'timeOutBySeller',
    outputs: [],
  },
  {
    stateMutability: 'view',
    type: 'function',
    inputs: [{ name: '', internalType: 'uint256', type: 'uint256' }],
    name: 'transactions',
    outputs: [
      { name: 'buyer', internalType: 'address payable', type: 'address' },
      { name: 'seller', internalType: 'address payable', type: 'address' },
      { name: 'amount', internalType: 'uint256', type: 'uint256' },
      { name: 'settlementBuyer', internalType: 'uint256', type: 'uint256' },
      { name: 'settlementSeller', internalType: 'uint256', type: 'uint256' },
      { name: 'deadline', internalType: 'uint256', type: 'uint256' },
      { name: 'disputeID', internalType: 'uint256', type: 'uint256' },
      { name: 'buyerFee', internalType: 'uint256', type: 'uint256' },
      { name: 'sellerFee', internalType: 'uint256', type: 'uint256' },
      { name: 'lastFeePaymentTime', internalType: 'uint256', type: 'uint256' },
      { name: 'templateData', internalType: 'string', type: 'string' },
      { name: 'templateDataMappings', internalType: 'string', type: 'string' },
      { name: 'status', internalType: 'enum Status', type: 'uint8' },
      { name: 'token', internalType: 'contract IERC20', type: 'address' },
    ],
  },
] as const

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export const escrowUniversalAddress = {
  421614: '0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De',
} as const

/**
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export const escrowUniversalConfig = { address: escrowUniversalAddress, abi: escrowUniversalABI } as const

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// React
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowRead<
  TFunctionName extends string,
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractRead({ abi: escrowABI, address: escrowAddress[421614], ...config } as UseContractReadConfig<
    typeof escrowABI,
    TFunctionName,
    TSelectData
  >)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"AMOUNT_OF_CHOICES"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowAmountOfChoices<
  TFunctionName extends 'AMOUNT_OF_CHOICES',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'AMOUNT_OF_CHOICES',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"arbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowArbitrator<
  TFunctionName extends 'arbitrator',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'arbitrator',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"arbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowArbitratorExtraData<
  TFunctionName extends 'arbitratorExtraData',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'arbitratorExtraData',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"disputeIDtoTransactionID"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowDisputeIDtoTransactionId<
  TFunctionName extends 'disputeIDtoTransactionID',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'disputeIDtoTransactionID',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"feeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowFeeTimeout<
  TFunctionName extends 'feeTimeout',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'feeTimeout',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"getCountTransactions"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowGetCountTransactions<
  TFunctionName extends 'getCountTransactions',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'getCountTransactions',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"governor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowGovernor<
  TFunctionName extends 'governor',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'governor',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"settlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowSettlementTimeout<
  TFunctionName extends 'settlementTimeout',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'settlementTimeout',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"templateId"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowTemplateId<
  TFunctionName extends 'templateId',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'templateId',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"templateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowTemplateRegistry<
  TFunctionName extends 'templateRegistry',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'templateRegistry',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"transactions"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowTransactions<
  TFunctionName extends 'transactions',
  TSelectData = ReadContractResult<typeof escrowABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return useContractRead({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'transactions',
    ...config,
  } as UseContractReadConfig<typeof escrowABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowWrite<
  TFunctionName extends string,
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, string>['request']['abi'],
        TFunctionName,
        TMode
      > & { address?: Address; chainId?: TChainId }
    : UseContractWriteConfig<typeof escrowABI, TFunctionName, TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, TFunctionName, TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"acceptSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowAcceptSettlement<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'acceptSettlement'>['request']['abi'],
        'acceptSettlement',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'acceptSettlement' }
    : UseContractWriteConfig<typeof escrowABI, 'acceptSettlement', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'acceptSettlement'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'acceptSettlement', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'acceptSettlement',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeArbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowChangeArbitrator<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'changeArbitrator'>['request']['abi'],
        'changeArbitrator',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeArbitrator' }
    : UseContractWriteConfig<typeof escrowABI, 'changeArbitrator', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeArbitrator'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'changeArbitrator', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeArbitrator',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeArbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowChangeArbitratorExtraData<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'changeArbitratorExtraData'>['request']['abi'],
        'changeArbitratorExtraData',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeArbitratorExtraData' }
    : UseContractWriteConfig<typeof escrowABI, 'changeArbitratorExtraData', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeArbitratorExtraData'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'changeArbitratorExtraData', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeArbitratorExtraData',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeDisputeTemplate"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowChangeDisputeTemplate<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'changeDisputeTemplate'>['request']['abi'],
        'changeDisputeTemplate',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeDisputeTemplate' }
    : UseContractWriteConfig<typeof escrowABI, 'changeDisputeTemplate', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeDisputeTemplate'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'changeDisputeTemplate', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeDisputeTemplate',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeFeeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowChangeFeeTimeout<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'changeFeeTimeout'>['request']['abi'],
        'changeFeeTimeout',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeFeeTimeout' }
    : UseContractWriteConfig<typeof escrowABI, 'changeFeeTimeout', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeFeeTimeout'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'changeFeeTimeout', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeFeeTimeout',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeGovernor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowChangeGovernor<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'changeGovernor'>['request']['abi'],
        'changeGovernor',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeGovernor' }
    : UseContractWriteConfig<typeof escrowABI, 'changeGovernor', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeGovernor'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'changeGovernor', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeGovernor',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeSettlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowChangeSettlementTimeout<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'changeSettlementTimeout'>['request']['abi'],
        'changeSettlementTimeout',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeSettlementTimeout' }
    : UseContractWriteConfig<typeof escrowABI, 'changeSettlementTimeout', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeSettlementTimeout'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'changeSettlementTimeout', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeSettlementTimeout',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeTemplateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowChangeTemplateRegistry<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'changeTemplateRegistry'>['request']['abi'],
        'changeTemplateRegistry',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeTemplateRegistry' }
    : UseContractWriteConfig<typeof escrowABI, 'changeTemplateRegistry', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeTemplateRegistry'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'changeTemplateRegistry', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeTemplateRegistry',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"createTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowCreateTransaction<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'createTransaction'>['request']['abi'],
        'createTransaction',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'createTransaction' }
    : UseContractWriteConfig<typeof escrowABI, 'createTransaction', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'createTransaction'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'createTransaction', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'createTransaction',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"executeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowExecuteTransaction<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'executeTransaction'>['request']['abi'],
        'executeTransaction',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'executeTransaction' }
    : UseContractWriteConfig<typeof escrowABI, 'executeTransaction', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'executeTransaction'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'executeTransaction', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'executeTransaction',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"pay"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowPay<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<PrepareWriteContractResult<typeof escrowABI, 'pay'>['request']['abi'], 'pay', TMode> & {
        address?: Address
        chainId?: TChainId
        functionName?: 'pay'
      }
    : UseContractWriteConfig<typeof escrowABI, 'pay', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'pay'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'pay', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'pay',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"payArbitrationFeeByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowPayArbitrationFeeByBuyer<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'payArbitrationFeeByBuyer'>['request']['abi'],
        'payArbitrationFeeByBuyer',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'payArbitrationFeeByBuyer' }
    : UseContractWriteConfig<typeof escrowABI, 'payArbitrationFeeByBuyer', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'payArbitrationFeeByBuyer'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'payArbitrationFeeByBuyer', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'payArbitrationFeeByBuyer',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"payArbitrationFeeBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowPayArbitrationFeeBySeller<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'payArbitrationFeeBySeller'>['request']['abi'],
        'payArbitrationFeeBySeller',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'payArbitrationFeeBySeller' }
    : UseContractWriteConfig<typeof escrowABI, 'payArbitrationFeeBySeller', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'payArbitrationFeeBySeller'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'payArbitrationFeeBySeller', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'payArbitrationFeeBySeller',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"proposeSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowProposeSettlement<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'proposeSettlement'>['request']['abi'],
        'proposeSettlement',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'proposeSettlement' }
    : UseContractWriteConfig<typeof escrowABI, 'proposeSettlement', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'proposeSettlement'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'proposeSettlement', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'proposeSettlement',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"reimburse"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowReimburse<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'reimburse'>['request']['abi'],
        'reimburse',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'reimburse' }
    : UseContractWriteConfig<typeof escrowABI, 'reimburse', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'reimburse'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'reimburse', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'reimburse',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"rule"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowRule<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<PrepareWriteContractResult<typeof escrowABI, 'rule'>['request']['abi'], 'rule', TMode> & {
        address?: Address
        chainId?: TChainId
        functionName?: 'rule'
      }
    : UseContractWriteConfig<typeof escrowABI, 'rule', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'rule'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'rule', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'rule',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"timeOutByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowTimeOutByBuyer<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'timeOutByBuyer'>['request']['abi'],
        'timeOutByBuyer',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'timeOutByBuyer' }
    : UseContractWriteConfig<typeof escrowABI, 'timeOutByBuyer', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'timeOutByBuyer'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'timeOutByBuyer', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'timeOutByBuyer',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"timeOutBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowTimeOutBySeller<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowABI, 'timeOutBySeller'>['request']['abi'],
        'timeOutBySeller',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'timeOutBySeller' }
    : UseContractWriteConfig<typeof escrowABI, 'timeOutBySeller', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'timeOutBySeller'
      } = {} as any
) {
  return useContractWrite<typeof escrowABI, 'timeOutBySeller', TMode>({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'timeOutBySeller',
    ...config,
  } as any)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowWrite<TFunctionName extends string>(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowABI, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, TFunctionName>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"acceptSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowAcceptSettlement(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'acceptSettlement'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'acceptSettlement',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'acceptSettlement'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeArbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowChangeArbitrator(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'changeArbitrator'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeArbitrator',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'changeArbitrator'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeArbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowChangeArbitratorExtraData(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'changeArbitratorExtraData'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeArbitratorExtraData',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'changeArbitratorExtraData'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeDisputeTemplate"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowChangeDisputeTemplate(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'changeDisputeTemplate'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeDisputeTemplate',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'changeDisputeTemplate'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeFeeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowChangeFeeTimeout(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'changeFeeTimeout'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeFeeTimeout',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'changeFeeTimeout'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeGovernor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowChangeGovernor(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'changeGovernor'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeGovernor',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'changeGovernor'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeSettlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowChangeSettlementTimeout(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'changeSettlementTimeout'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeSettlementTimeout',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'changeSettlementTimeout'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"changeTemplateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowChangeTemplateRegistry(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'changeTemplateRegistry'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'changeTemplateRegistry',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'changeTemplateRegistry'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"createTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowCreateTransaction(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'createTransaction'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'createTransaction',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'createTransaction'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"executeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowExecuteTransaction(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'executeTransaction'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'executeTransaction',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'executeTransaction'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"pay"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowPay(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowABI, 'pay'>, 'abi' | 'address' | 'functionName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'pay',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'pay'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"payArbitrationFeeByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowPayArbitrationFeeByBuyer(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'payArbitrationFeeByBuyer'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'payArbitrationFeeByBuyer',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'payArbitrationFeeByBuyer'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"payArbitrationFeeBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowPayArbitrationFeeBySeller(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'payArbitrationFeeBySeller'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'payArbitrationFeeBySeller',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'payArbitrationFeeBySeller'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"proposeSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowProposeSettlement(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'proposeSettlement'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'proposeSettlement',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'proposeSettlement'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"reimburse"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowReimburse(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowABI, 'reimburse'>, 'abi' | 'address' | 'functionName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'reimburse',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'reimburse'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"rule"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowRule(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowABI, 'rule'>, 'abi' | 'address' | 'functionName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'rule',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'rule'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"timeOutByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowTimeOutByBuyer(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'timeOutByBuyer'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'timeOutByBuyer',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'timeOutByBuyer'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowABI}__ and `functionName` set to `"timeOutBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function usePrepareEscrowTimeOutBySeller(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowABI, 'timeOutBySeller'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowABI,
    address: escrowAddress[421614],
    functionName: 'timeOutBySeller',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowABI, 'timeOutBySeller'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowEvent<TEventName extends string>(
  config: Omit<UseContractEventConfig<typeof escrowABI, TEventName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({ abi: escrowABI, address: escrowAddress[421614], ...config } as UseContractEventConfig<
    typeof escrowABI,
    TEventName
  >)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"DisputeRequest"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowDisputeRequestEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'DisputeRequest'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'DisputeRequest',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'DisputeRequest'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"HasToPayFee"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowHasToPayFeeEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'HasToPayFee'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'HasToPayFee',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'HasToPayFee'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"ParameterUpdated"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowParameterUpdatedEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'ParameterUpdated'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'ParameterUpdated',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'ParameterUpdated'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"Payment"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowPaymentEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'Payment'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'Payment',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'Payment'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"Ruling"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowRulingEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'Ruling'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'Ruling',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'Ruling'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"SettlementProposed"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowSettlementProposedEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'SettlementProposed'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'SettlementProposed',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'SettlementProposed'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"TransactionCreated"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowTransactionCreatedEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'TransactionCreated'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'TransactionCreated',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'TransactionCreated'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowABI}__ and `eventName` set to `"TransactionResolved"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function useEscrowTransactionResolvedEvent(
  config: Omit<UseContractEventConfig<typeof escrowABI, 'TransactionResolved'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowABI,
    address: escrowAddress[421614],
    eventName: 'TransactionResolved',
    ...config,
  } as UseContractEventConfig<typeof escrowABI, 'TransactionResolved'>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenRead<
  TFunctionName extends string,
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"AMOUNT_OF_CHOICES"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenAmountOfChoices<
  TFunctionName extends 'AMOUNT_OF_CHOICES',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'AMOUNT_OF_CHOICES',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"arbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenArbitrator<
  TFunctionName extends 'arbitrator',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'arbitrator',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"arbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenArbitratorExtraData<
  TFunctionName extends 'arbitratorExtraData',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'arbitratorExtraData',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"disputeIDtoTransactionID"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenDisputeIDtoTransactionId<
  TFunctionName extends 'disputeIDtoTransactionID',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'disputeIDtoTransactionID',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"feeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenFeeTimeout<
  TFunctionName extends 'feeTimeout',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'feeTimeout',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"getCountTransactions"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenGetCountTransactions<
  TFunctionName extends 'getCountTransactions',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'getCountTransactions',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"governor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenGovernor<
  TFunctionName extends 'governor',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'governor',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"settlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenSettlementTimeout<
  TFunctionName extends 'settlementTimeout',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'settlementTimeout',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"templateId"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenTemplateId<
  TFunctionName extends 'templateId',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'templateId',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"templateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenTemplateRegistry<
  TFunctionName extends 'templateRegistry',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'templateRegistry',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"transactions"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenTransactions<
  TFunctionName extends 'transactions',
  TSelectData = ReadContractResult<typeof escrowTokenABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractRead({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'transactions',
    ...config,
  } as UseContractReadConfig<typeof escrowTokenABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenWrite<
  TFunctionName extends string,
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, string>['request']['abi'],
        TFunctionName,
        TMode
      > & { address?: Address; chainId?: TChainId }
    : UseContractWriteConfig<typeof escrowTokenABI, TFunctionName, TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, TFunctionName, TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"acceptSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenAcceptSettlement<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'acceptSettlement'>['request']['abi'],
        'acceptSettlement',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'acceptSettlement' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'acceptSettlement', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'acceptSettlement'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'acceptSettlement', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'acceptSettlement',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeArbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenChangeArbitrator<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'changeArbitrator'>['request']['abi'],
        'changeArbitrator',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeArbitrator' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'changeArbitrator', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeArbitrator'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'changeArbitrator', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeArbitrator',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeArbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenChangeArbitratorExtraData<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'changeArbitratorExtraData'>['request']['abi'],
        'changeArbitratorExtraData',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeArbitratorExtraData' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'changeArbitratorExtraData', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeArbitratorExtraData'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'changeArbitratorExtraData', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeArbitratorExtraData',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeDisputeTemplate"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenChangeDisputeTemplate<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'changeDisputeTemplate'>['request']['abi'],
        'changeDisputeTemplate',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeDisputeTemplate' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'changeDisputeTemplate', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeDisputeTemplate'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'changeDisputeTemplate', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeDisputeTemplate',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeFeeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenChangeFeeTimeout<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'changeFeeTimeout'>['request']['abi'],
        'changeFeeTimeout',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeFeeTimeout' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'changeFeeTimeout', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeFeeTimeout'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'changeFeeTimeout', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeFeeTimeout',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeGovernor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenChangeGovernor<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'changeGovernor'>['request']['abi'],
        'changeGovernor',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeGovernor' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'changeGovernor', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeGovernor'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'changeGovernor', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeGovernor',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeSettlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenChangeSettlementTimeout<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'changeSettlementTimeout'>['request']['abi'],
        'changeSettlementTimeout',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeSettlementTimeout' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'changeSettlementTimeout', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeSettlementTimeout'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'changeSettlementTimeout', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeSettlementTimeout',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeTemplateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenChangeTemplateRegistry<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'changeTemplateRegistry'>['request']['abi'],
        'changeTemplateRegistry',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeTemplateRegistry' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'changeTemplateRegistry', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeTemplateRegistry'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'changeTemplateRegistry', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeTemplateRegistry',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"createTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenCreateTransaction<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'createTransaction'>['request']['abi'],
        'createTransaction',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'createTransaction' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'createTransaction', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'createTransaction'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'createTransaction', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'createTransaction',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"executeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenExecuteTransaction<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'executeTransaction'>['request']['abi'],
        'executeTransaction',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'executeTransaction' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'executeTransaction', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'executeTransaction'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'executeTransaction', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'executeTransaction',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"pay"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenPay<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'pay'>['request']['abi'],
        'pay',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'pay' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'pay', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'pay'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'pay', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'pay',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"payArbitrationFeeByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenPayArbitrationFeeByBuyer<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'payArbitrationFeeByBuyer'>['request']['abi'],
        'payArbitrationFeeByBuyer',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'payArbitrationFeeByBuyer' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'payArbitrationFeeByBuyer', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'payArbitrationFeeByBuyer'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'payArbitrationFeeByBuyer', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'payArbitrationFeeByBuyer',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"payArbitrationFeeBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenPayArbitrationFeeBySeller<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'payArbitrationFeeBySeller'>['request']['abi'],
        'payArbitrationFeeBySeller',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'payArbitrationFeeBySeller' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'payArbitrationFeeBySeller', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'payArbitrationFeeBySeller'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'payArbitrationFeeBySeller', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'payArbitrationFeeBySeller',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"proposeSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenProposeSettlement<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'proposeSettlement'>['request']['abi'],
        'proposeSettlement',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'proposeSettlement' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'proposeSettlement', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'proposeSettlement'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'proposeSettlement', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'proposeSettlement',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"reimburse"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenReimburse<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'reimburse'>['request']['abi'],
        'reimburse',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'reimburse' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'reimburse', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'reimburse'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'reimburse', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'reimburse',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"rule"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenRule<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'rule'>['request']['abi'],
        'rule',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'rule' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'rule', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'rule'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'rule', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'rule',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"timeOutByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenTimeOutByBuyer<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'timeOutByBuyer'>['request']['abi'],
        'timeOutByBuyer',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'timeOutByBuyer' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'timeOutByBuyer', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'timeOutByBuyer'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'timeOutByBuyer', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'timeOutByBuyer',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"timeOutBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenTimeOutBySeller<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowTokenABI, 'timeOutBySeller'>['request']['abi'],
        'timeOutBySeller',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'timeOutBySeller' }
    : UseContractWriteConfig<typeof escrowTokenABI, 'timeOutBySeller', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'timeOutBySeller'
      } = {} as any
) {
  return useContractWrite<typeof escrowTokenABI, 'timeOutBySeller', TMode>({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'timeOutBySeller',
    ...config,
  } as any)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenWrite<TFunctionName extends string>(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowTokenABI, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, TFunctionName>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"acceptSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenAcceptSettlement(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'acceptSettlement'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'acceptSettlement',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'acceptSettlement'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeArbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenChangeArbitrator(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeArbitrator'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeArbitrator',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeArbitrator'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeArbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenChangeArbitratorExtraData(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeArbitratorExtraData'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeArbitratorExtraData',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeArbitratorExtraData'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeDisputeTemplate"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenChangeDisputeTemplate(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeDisputeTemplate'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeDisputeTemplate',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeDisputeTemplate'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeFeeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenChangeFeeTimeout(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeFeeTimeout'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeFeeTimeout',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeFeeTimeout'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeGovernor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenChangeGovernor(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeGovernor'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeGovernor',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeGovernor'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeSettlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenChangeSettlementTimeout(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeSettlementTimeout'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeSettlementTimeout',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeSettlementTimeout'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"changeTemplateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenChangeTemplateRegistry(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeTemplateRegistry'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'changeTemplateRegistry',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'changeTemplateRegistry'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"createTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenCreateTransaction(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'createTransaction'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'createTransaction',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'createTransaction'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"executeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenExecuteTransaction(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'executeTransaction'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'executeTransaction',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'executeTransaction'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"pay"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenPay(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowTokenABI, 'pay'>, 'abi' | 'address' | 'functionName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'pay',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'pay'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"payArbitrationFeeByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenPayArbitrationFeeByBuyer(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'payArbitrationFeeByBuyer'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'payArbitrationFeeByBuyer',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'payArbitrationFeeByBuyer'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"payArbitrationFeeBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenPayArbitrationFeeBySeller(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'payArbitrationFeeBySeller'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'payArbitrationFeeBySeller',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'payArbitrationFeeBySeller'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"proposeSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenProposeSettlement(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'proposeSettlement'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'proposeSettlement',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'proposeSettlement'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"reimburse"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenReimburse(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'reimburse'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'reimburse',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'reimburse'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"rule"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenRule(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowTokenABI, 'rule'>, 'abi' | 'address' | 'functionName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'rule',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'rule'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"timeOutByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenTimeOutByBuyer(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'timeOutByBuyer'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'timeOutByBuyer',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'timeOutByBuyer'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowTokenABI}__ and `functionName` set to `"timeOutBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function usePrepareEscrowTokenTimeOutBySeller(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowTokenABI, 'timeOutBySeller'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    functionName: 'timeOutBySeller',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowTokenABI, 'timeOutBySeller'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenEvent<TEventName extends string>(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, TEventName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, TEventName>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"DisputeRequest"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenDisputeRequestEvent(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, 'DisputeRequest'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'DisputeRequest',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'DisputeRequest'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"HasToPayFee"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenHasToPayFeeEvent(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, 'HasToPayFee'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'HasToPayFee',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'HasToPayFee'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"ParameterUpdated"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenParameterUpdatedEvent(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, 'ParameterUpdated'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'ParameterUpdated',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'ParameterUpdated'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"Payment"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenPaymentEvent(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, 'Payment'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'Payment',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'Payment'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"Ruling"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenRulingEvent(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, 'Ruling'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'Ruling',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'Ruling'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"SettlementProposed"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenSettlementProposedEvent(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, 'SettlementProposed'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'SettlementProposed',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'SettlementProposed'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"TransactionCreated"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenTransactionCreatedEvent(
  config: Omit<UseContractEventConfig<typeof escrowTokenABI, 'TransactionCreated'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowTokenAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'TransactionCreated',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'TransactionCreated'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowTokenABI}__ and `eventName` set to `"TransactionResolved"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function useEscrowTokenTransactionResolvedEvent(
  config: Omit<
    UseContractEventConfig<typeof escrowTokenABI, 'TransactionResolved'>,
    'abi' | 'address' | 'eventName'
  > & { chainId?: keyof typeof escrowTokenAddress } = {} as any
) {
  return useContractEvent({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    eventName: 'TransactionResolved',
    ...config,
  } as UseContractEventConfig<typeof escrowTokenABI, 'TransactionResolved'>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalRead<
  TFunctionName extends string,
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"AMOUNT_OF_CHOICES"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalAmountOfChoices<
  TFunctionName extends 'AMOUNT_OF_CHOICES',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'AMOUNT_OF_CHOICES',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"arbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalArbitrator<
  TFunctionName extends 'arbitrator',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'arbitrator',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"arbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalArbitratorExtraData<
  TFunctionName extends 'arbitratorExtraData',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'arbitratorExtraData',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"disputeIDtoTransactionID"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalDisputeIDtoTransactionId<
  TFunctionName extends 'disputeIDtoTransactionID',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'disputeIDtoTransactionID',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"feeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalFeeTimeout<
  TFunctionName extends 'feeTimeout',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'feeTimeout',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"getTransactionCount"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalGetTransactionCount<
  TFunctionName extends 'getTransactionCount',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'getTransactionCount',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"governor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalGovernor<
  TFunctionName extends 'governor',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'governor',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"settlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalSettlementTimeout<
  TFunctionName extends 'settlementTimeout',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'settlementTimeout',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"templateId"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalTemplateId<
  TFunctionName extends 'templateId',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'templateId',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"templateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalTemplateRegistry<
  TFunctionName extends 'templateRegistry',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'templateRegistry',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractRead}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"transactions"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalTransactions<
  TFunctionName extends 'transactions',
  TSelectData = ReadContractResult<typeof escrowUniversalABI, TFunctionName>
>(
  config: Omit<
    UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractRead({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'transactions',
    ...config,
  } as UseContractReadConfig<typeof escrowUniversalABI, TFunctionName, TSelectData>)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalWrite<
  TFunctionName extends string,
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, string>['request']['abi'],
        TFunctionName,
        TMode
      > & { address?: Address; chainId?: TChainId }
    : UseContractWriteConfig<typeof escrowUniversalABI, TFunctionName, TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, TFunctionName, TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"acceptSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalAcceptSettlement<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'acceptSettlement'>['request']['abi'],
        'acceptSettlement',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'acceptSettlement' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'acceptSettlement', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'acceptSettlement'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'acceptSettlement', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'acceptSettlement',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeArbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalChangeArbitrator<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'changeArbitrator'>['request']['abi'],
        'changeArbitrator',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeArbitrator' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'changeArbitrator', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeArbitrator'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'changeArbitrator', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeArbitrator',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeArbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalChangeArbitratorExtraData<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'changeArbitratorExtraData'>['request']['abi'],
        'changeArbitratorExtraData',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeArbitratorExtraData' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'changeArbitratorExtraData', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeArbitratorExtraData'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'changeArbitratorExtraData', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeArbitratorExtraData',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeDisputeTemplate"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalChangeDisputeTemplate<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'changeDisputeTemplate'>['request']['abi'],
        'changeDisputeTemplate',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeDisputeTemplate' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'changeDisputeTemplate', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeDisputeTemplate'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'changeDisputeTemplate', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeDisputeTemplate',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeFeeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalChangeFeeTimeout<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'changeFeeTimeout'>['request']['abi'],
        'changeFeeTimeout',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeFeeTimeout' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'changeFeeTimeout', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeFeeTimeout'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'changeFeeTimeout', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeFeeTimeout',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeGovernor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalChangeGovernor<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'changeGovernor'>['request']['abi'],
        'changeGovernor',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeGovernor' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'changeGovernor', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeGovernor'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'changeGovernor', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeGovernor',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeSettlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalChangeSettlementTimeout<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'changeSettlementTimeout'>['request']['abi'],
        'changeSettlementTimeout',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeSettlementTimeout' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'changeSettlementTimeout', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeSettlementTimeout'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'changeSettlementTimeout', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeSettlementTimeout',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeTemplateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalChangeTemplateRegistry<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'changeTemplateRegistry'>['request']['abi'],
        'changeTemplateRegistry',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'changeTemplateRegistry' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'changeTemplateRegistry', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'changeTemplateRegistry'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'changeTemplateRegistry', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeTemplateRegistry',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"createERC20Transaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalCreateErc20Transaction<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'createERC20Transaction'>['request']['abi'],
        'createERC20Transaction',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'createERC20Transaction' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'createERC20Transaction', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'createERC20Transaction'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'createERC20Transaction', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'createERC20Transaction',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"createNativeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalCreateNativeTransaction<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'createNativeTransaction'>['request']['abi'],
        'createNativeTransaction',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'createNativeTransaction' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'createNativeTransaction', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'createNativeTransaction'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'createNativeTransaction', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'createNativeTransaction',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"executeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalExecuteTransaction<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'executeTransaction'>['request']['abi'],
        'executeTransaction',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'executeTransaction' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'executeTransaction', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'executeTransaction'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'executeTransaction', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'executeTransaction',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"pay"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalPay<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'pay'>['request']['abi'],
        'pay',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'pay' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'pay', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'pay'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'pay', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'pay',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"payArbitrationFeeByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalPayArbitrationFeeByBuyer<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'payArbitrationFeeByBuyer'>['request']['abi'],
        'payArbitrationFeeByBuyer',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'payArbitrationFeeByBuyer' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'payArbitrationFeeByBuyer', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'payArbitrationFeeByBuyer'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'payArbitrationFeeByBuyer', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'payArbitrationFeeByBuyer',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"payArbitrationFeeBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalPayArbitrationFeeBySeller<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'payArbitrationFeeBySeller'>['request']['abi'],
        'payArbitrationFeeBySeller',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'payArbitrationFeeBySeller' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'payArbitrationFeeBySeller', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'payArbitrationFeeBySeller'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'payArbitrationFeeBySeller', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'payArbitrationFeeBySeller',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"proposeSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalProposeSettlement<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'proposeSettlement'>['request']['abi'],
        'proposeSettlement',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'proposeSettlement' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'proposeSettlement', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'proposeSettlement'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'proposeSettlement', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'proposeSettlement',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"reimburse"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalReimburse<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'reimburse'>['request']['abi'],
        'reimburse',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'reimburse' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'reimburse', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'reimburse'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'reimburse', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'reimburse',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"rule"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalRule<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'rule'>['request']['abi'],
        'rule',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'rule' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'rule', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'rule'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'rule', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'rule',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"timeOutByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalTimeOutByBuyer<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'timeOutByBuyer'>['request']['abi'],
        'timeOutByBuyer',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'timeOutByBuyer' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'timeOutByBuyer', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'timeOutByBuyer'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'timeOutByBuyer', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'timeOutByBuyer',
    ...config,
  } as any)
}

/**
 * Wraps __{@link useContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"timeOutBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalTimeOutBySeller<
  TMode extends WriteContractMode = undefined,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config: TMode extends 'prepared'
    ? UseContractWriteConfig<
        PrepareWriteContractResult<typeof escrowUniversalABI, 'timeOutBySeller'>['request']['abi'],
        'timeOutBySeller',
        TMode
      > & { address?: Address; chainId?: TChainId; functionName?: 'timeOutBySeller' }
    : UseContractWriteConfig<typeof escrowUniversalABI, 'timeOutBySeller', TMode> & {
        abi?: never
        address?: never
        chainId?: TChainId
        functionName?: 'timeOutBySeller'
      } = {} as any
) {
  return useContractWrite<typeof escrowUniversalABI, 'timeOutBySeller', TMode>({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'timeOutBySeller',
    ...config,
  } as any)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalWrite<TFunctionName extends string>(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowUniversalABI, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, TFunctionName>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"acceptSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalAcceptSettlement(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'acceptSettlement'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'acceptSettlement',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'acceptSettlement'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeArbitrator"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalChangeArbitrator(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeArbitrator'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeArbitrator',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeArbitrator'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeArbitratorExtraData"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalChangeArbitratorExtraData(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeArbitratorExtraData'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeArbitratorExtraData',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeArbitratorExtraData'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeDisputeTemplate"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalChangeDisputeTemplate(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeDisputeTemplate'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeDisputeTemplate',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeDisputeTemplate'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeFeeTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalChangeFeeTimeout(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeFeeTimeout'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeFeeTimeout',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeFeeTimeout'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeGovernor"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalChangeGovernor(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeGovernor'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeGovernor',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeGovernor'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeSettlementTimeout"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalChangeSettlementTimeout(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeSettlementTimeout'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeSettlementTimeout',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeSettlementTimeout'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"changeTemplateRegistry"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalChangeTemplateRegistry(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeTemplateRegistry'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'changeTemplateRegistry',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'changeTemplateRegistry'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"createERC20Transaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalCreateErc20Transaction(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'createERC20Transaction'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'createERC20Transaction',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'createERC20Transaction'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"createNativeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalCreateNativeTransaction(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'createNativeTransaction'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'createNativeTransaction',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'createNativeTransaction'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"executeTransaction"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalExecuteTransaction(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'executeTransaction'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'executeTransaction',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'executeTransaction'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"pay"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalPay(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'pay'>, 'abi' | 'address' | 'functionName'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'pay',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'pay'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"payArbitrationFeeByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalPayArbitrationFeeByBuyer(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'payArbitrationFeeByBuyer'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'payArbitrationFeeByBuyer',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'payArbitrationFeeByBuyer'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"payArbitrationFeeBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalPayArbitrationFeeBySeller(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'payArbitrationFeeBySeller'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'payArbitrationFeeBySeller',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'payArbitrationFeeBySeller'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"proposeSettlement"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalProposeSettlement(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'proposeSettlement'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'proposeSettlement',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'proposeSettlement'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"reimburse"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalReimburse(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'reimburse'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'reimburse',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'reimburse'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"rule"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalRule(
  config: Omit<UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'rule'>, 'abi' | 'address' | 'functionName'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'rule',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'rule'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"timeOutByBuyer"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalTimeOutByBuyer(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'timeOutByBuyer'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'timeOutByBuyer',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'timeOutByBuyer'>)
}

/**
 * Wraps __{@link usePrepareContractWrite}__ with `abi` set to __{@link escrowUniversalABI}__ and `functionName` set to `"timeOutBySeller"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function usePrepareEscrowUniversalTimeOutBySeller(
  config: Omit<
    UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'timeOutBySeller'>,
    'abi' | 'address' | 'functionName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return usePrepareContractWrite({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    functionName: 'timeOutBySeller',
    ...config,
  } as UsePrepareContractWriteConfig<typeof escrowUniversalABI, 'timeOutBySeller'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalEvent<TEventName extends string>(
  config: Omit<UseContractEventConfig<typeof escrowUniversalABI, TEventName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, TEventName>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"DisputeRequest"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalDisputeRequestEvent(
  config: Omit<UseContractEventConfig<typeof escrowUniversalABI, 'DisputeRequest'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'DisputeRequest',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'DisputeRequest'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"ERC20TransactionCreated"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalErc20TransactionCreatedEvent(
  config: Omit<
    UseContractEventConfig<typeof escrowUniversalABI, 'ERC20TransactionCreated'>,
    'abi' | 'address' | 'eventName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'ERC20TransactionCreated',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'ERC20TransactionCreated'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"HasToPayFee"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalHasToPayFeeEvent(
  config: Omit<UseContractEventConfig<typeof escrowUniversalABI, 'HasToPayFee'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'HasToPayFee',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'HasToPayFee'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"NativeTransactionCreated"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalNativeTransactionCreatedEvent(
  config: Omit<
    UseContractEventConfig<typeof escrowUniversalABI, 'NativeTransactionCreated'>,
    'abi' | 'address' | 'eventName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'NativeTransactionCreated',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'NativeTransactionCreated'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"ParameterUpdated"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalParameterUpdatedEvent(
  config: Omit<
    UseContractEventConfig<typeof escrowUniversalABI, 'ParameterUpdated'>,
    'abi' | 'address' | 'eventName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'ParameterUpdated',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'ParameterUpdated'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"Payment"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalPaymentEvent(
  config: Omit<UseContractEventConfig<typeof escrowUniversalABI, 'Payment'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'Payment',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'Payment'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"Ruling"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalRulingEvent(
  config: Omit<UseContractEventConfig<typeof escrowUniversalABI, 'Ruling'>, 'abi' | 'address' | 'eventName'> & {
    chainId?: keyof typeof escrowUniversalAddress
  } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'Ruling',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'Ruling'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"SettlementProposed"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalSettlementProposedEvent(
  config: Omit<
    UseContractEventConfig<typeof escrowUniversalABI, 'SettlementProposed'>,
    'abi' | 'address' | 'eventName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'SettlementProposed',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'SettlementProposed'>)
}

/**
 * Wraps __{@link useContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__ and `eventName` set to `"TransactionResolved"`.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function useEscrowUniversalTransactionResolvedEvent(
  config: Omit<
    UseContractEventConfig<typeof escrowUniversalABI, 'TransactionResolved'>,
    'abi' | 'address' | 'eventName'
  > & { chainId?: keyof typeof escrowUniversalAddress } = {} as any
) {
  return useContractEvent({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    eventName: 'TransactionResolved',
    ...config,
  } as UseContractEventConfig<typeof escrowUniversalABI, 'TransactionResolved'>)
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Core
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Wraps __{@link getContract}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function getEscrow(config: Omit<GetContractArgs, 'abi' | 'address'> & { chainId?: keyof typeof escrowAddress }) {
  return getContract({ abi: escrowABI, address: escrowAddress[421614], ...config })
}

/**
 * Wraps __{@link readContract}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function readEscrow<TAbi extends readonly unknown[] = typeof escrowABI, TFunctionName extends string = string>(
  config: Omit<ReadContractConfig<TAbi, TFunctionName>, 'abi' | 'address'> & { chainId?: keyof typeof escrowAddress }
) {
  return readContract({ abi: escrowABI, address: escrowAddress[421614], ...config } as unknown as ReadContractConfig<
    TAbi,
    TFunctionName
  >)
}

/**
 * Wraps __{@link writeContract}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function writeEscrow<
  TFunctionName extends string,
  TMode extends WriteContractMode,
  TChainId extends number = keyof typeof escrowAddress
>(
  config:
    | (Omit<WriteContractPreparedArgs<typeof escrowABI, TFunctionName>, 'abi' | 'address'> & {
        mode: TMode
        chainId?: TMode extends 'prepared' ? TChainId : keyof typeof escrowAddress
      })
    | (Omit<WriteContractUnpreparedArgs<typeof escrowABI, TFunctionName>, 'abi' | 'address'> & {
        mode: TMode
        chainId?: TMode extends 'prepared' ? TChainId : keyof typeof escrowAddress
      })
) {
  return writeContract({ abi: escrowABI, address: escrowAddress[421614], ...config } as unknown as WriteContractArgs<
    typeof escrowABI,
    TFunctionName
  >)
}

/**
 * Wraps __{@link prepareWriteContract}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function prepareWriteEscrow<
  TAbi extends readonly unknown[] = typeof escrowABI,
  TFunctionName extends string = string
>(
  config: Omit<PrepareWriteContractConfig<TAbi, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowAddress
  }
) {
  return prepareWriteContract({
    abi: escrowABI,
    address: escrowAddress[421614],
    ...config,
  } as unknown as PrepareWriteContractConfig<TAbi, TFunctionName>)
}

/**
 * Wraps __{@link watchContractEvent}__ with `abi` set to __{@link escrowABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0x250AB0477346aDFC010585b58FbF61cff1d8f3ea)
 */
export function watchEscrowEvent<
  TAbi extends readonly unknown[] = typeof escrowABI,
  TEventName extends string = string
>(
  config: Omit<WatchContractEventConfig<TAbi, TEventName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowAddress
  },
  callback: WatchContractEventCallback<TAbi, TEventName>
) {
  return watchContractEvent(
    { abi: escrowABI, address: escrowAddress[421614], ...config } as WatchContractEventConfig<TAbi, TEventName>,
    callback
  )
}

/**
 * Wraps __{@link getContract}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function getEscrowToken(
  config: Omit<GetContractArgs, 'abi' | 'address'> & { chainId?: keyof typeof escrowTokenAddress }
) {
  return getContract({ abi: escrowTokenABI, address: escrowTokenAddress[421614], ...config })
}

/**
 * Wraps __{@link readContract}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function readEscrowToken<
  TAbi extends readonly unknown[] = typeof escrowTokenABI,
  TFunctionName extends string = string
>(
  config: Omit<ReadContractConfig<TAbi, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowTokenAddress
  }
) {
  return readContract({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    ...config,
  } as unknown as ReadContractConfig<TAbi, TFunctionName>)
}

/**
 * Wraps __{@link writeContract}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function writeEscrowToken<
  TFunctionName extends string,
  TMode extends WriteContractMode,
  TChainId extends number = keyof typeof escrowTokenAddress
>(
  config:
    | (Omit<WriteContractPreparedArgs<typeof escrowTokenABI, TFunctionName>, 'abi' | 'address'> & {
        mode: TMode
        chainId?: TMode extends 'prepared' ? TChainId : keyof typeof escrowTokenAddress
      })
    | (Omit<WriteContractUnpreparedArgs<typeof escrowTokenABI, TFunctionName>, 'abi' | 'address'> & {
        mode: TMode
        chainId?: TMode extends 'prepared' ? TChainId : keyof typeof escrowTokenAddress
      })
) {
  return writeContract({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    ...config,
  } as unknown as WriteContractArgs<typeof escrowTokenABI, TFunctionName>)
}

/**
 * Wraps __{@link prepareWriteContract}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function prepareWriteEscrowToken<
  TAbi extends readonly unknown[] = typeof escrowTokenABI,
  TFunctionName extends string = string
>(
  config: Omit<PrepareWriteContractConfig<TAbi, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowTokenAddress
  }
) {
  return prepareWriteContract({
    abi: escrowTokenABI,
    address: escrowTokenAddress[421614],
    ...config,
  } as unknown as PrepareWriteContractConfig<TAbi, TFunctionName>)
}

/**
 * Wraps __{@link watchContractEvent}__ with `abi` set to __{@link escrowTokenABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xe7B52a7Bc50cb4Ff13d057629082844780b6E28D)
 */
export function watchEscrowTokenEvent<
  TAbi extends readonly unknown[] = typeof escrowTokenABI,
  TEventName extends string = string
>(
  config: Omit<WatchContractEventConfig<TAbi, TEventName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowTokenAddress
  },
  callback: WatchContractEventCallback<TAbi, TEventName>
) {
  return watchContractEvent(
    { abi: escrowTokenABI, address: escrowTokenAddress[421614], ...config } as WatchContractEventConfig<
      TAbi,
      TEventName
    >,
    callback
  )
}

/**
 * Wraps __{@link getContract}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function getEscrowUniversal(
  config: Omit<GetContractArgs, 'abi' | 'address'> & { chainId?: keyof typeof escrowUniversalAddress }
) {
  return getContract({ abi: escrowUniversalABI, address: escrowUniversalAddress[421614], ...config })
}

/**
 * Wraps __{@link readContract}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function readEscrowUniversal<
  TAbi extends readonly unknown[] = typeof escrowUniversalABI,
  TFunctionName extends string = string
>(
  config: Omit<ReadContractConfig<TAbi, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowUniversalAddress
  }
) {
  return readContract({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    ...config,
  } as unknown as ReadContractConfig<TAbi, TFunctionName>)
}

/**
 * Wraps __{@link writeContract}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function writeEscrowUniversal<
  TFunctionName extends string,
  TMode extends WriteContractMode,
  TChainId extends number = keyof typeof escrowUniversalAddress
>(
  config:
    | (Omit<WriteContractPreparedArgs<typeof escrowUniversalABI, TFunctionName>, 'abi' | 'address'> & {
        mode: TMode
        chainId?: TMode extends 'prepared' ? TChainId : keyof typeof escrowUniversalAddress
      })
    | (Omit<WriteContractUnpreparedArgs<typeof escrowUniversalABI, TFunctionName>, 'abi' | 'address'> & {
        mode: TMode
        chainId?: TMode extends 'prepared' ? TChainId : keyof typeof escrowUniversalAddress
      })
) {
  return writeContract({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    ...config,
  } as unknown as WriteContractArgs<typeof escrowUniversalABI, TFunctionName>)
}

/**
 * Wraps __{@link prepareWriteContract}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function prepareWriteEscrowUniversal<
  TAbi extends readonly unknown[] = typeof escrowUniversalABI,
  TFunctionName extends string = string
>(
  config: Omit<PrepareWriteContractConfig<TAbi, TFunctionName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowUniversalAddress
  }
) {
  return prepareWriteContract({
    abi: escrowUniversalABI,
    address: escrowUniversalAddress[421614],
    ...config,
  } as unknown as PrepareWriteContractConfig<TAbi, TFunctionName>)
}

/**
 * Wraps __{@link watchContractEvent}__ with `abi` set to __{@link escrowUniversalABI}__.
 *
 * [__View Contract on Arbitrum Sepolia Arbiscan__](https://sepolia.arbiscan.io/address/0xBa2cC03e2e34C5DEDaEbC481b2531e06946353De)
 */
export function watchEscrowUniversalEvent<
  TAbi extends readonly unknown[] = typeof escrowUniversalABI,
  TEventName extends string = string
>(
  config: Omit<WatchContractEventConfig<TAbi, TEventName>, 'abi' | 'address'> & {
    chainId?: keyof typeof escrowUniversalAddress
  },
  callback: WatchContractEventCallback<TAbi, TEventName>
) {
  return watchContractEvent(
    { abi: escrowUniversalABI, address: escrowUniversalAddress[421614], ...config } as WatchContractEventConfig<
      TAbi,
      TEventName
    >,
    callback
  )
}
